// Copyright 2022, Imprivata, Inc.  All rights reserved.

import type { ApiErrorPayloadCommon } from '@imprivata-cloud/common';
import type { CodingContext } from '@imprivata-cloud/data-privacy-js';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { usePlatform } from '../../context/platform/hooks';
import type { CreateSessionRequest, DigitalIdSession } from '../../models/digitalId';
import { apiClient } from '../client';

const createDigitalIdSessionRequest = async (
  request: CreateSessionRequest,
  codingContext: CodingContext | undefined,
): Promise<DigitalIdSession> => {
  // Validate input
  if (request.documentTypes.length === 0) {
    return Promise.reject({
      status: 400,
      body: { error: 'documentTypes array cannot be empty' },
    });
  }

  // Route through Patient Web Service gateway
  return apiClient.post<DigitalIdSession>(
    'digitalCredentials/sessions',
    () => request,
    codingContext,
  );
};

export const useCreateDigitalIdSessionMutation = (options: {
  onSuccess: (session: DigitalIdSession) => void;
}) => {
  const { codingContext } = usePlatform();
  const [apiError, setApiError] = useState<ApiErrorPayloadCommon | null>(null);
  const mutation = useMutation({
    mutationFn: (request: CreateSessionRequest) =>
      createDigitalIdSessionRequest(request, codingContext),
    onSuccess: options.onSuccess,
    onError: (error) => setApiError(error as ApiErrorPayloadCommon),
  });
  return {
    createSession: mutation.mutate,
    isLoading: mutation.isPending,
    apiError,
    resetApiError: () => setApiError(null),
  };
};
